import { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'

/**
 * Location Context Selector
 *
 * Breadcrumb-style navigation for switching between location contexts
 */
function LocationContextSelector({
  currentLocation,
  locationsData,
  onLocationChange,
}) {
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Build breadcrumb trail
  const getBreadcrumbs = () => {
    if (!currentLocation) return []

    const breadcrumbs = []

    // Always start with home/national level
    breadcrumbs.push({
      label: 'Uganda',
      id: 'national',
      level: 'national',
    })

    // Add current location's ancestors
    if (currentLocation.type === 'district') {
      breadcrumbs.push({
        label: currentLocation.name,
        id: currentLocation.id,
        level: 'district',
      })
    } else if (currentLocation.type === 'subcounty' && currentLocation.parent) {
      breadcrumbs.push({
        label: currentLocation.parent.name,
        id: currentLocation.parent.id,
        level: 'district',
      })
      breadcrumbs.push({
        label: currentLocation.name,
        id: currentLocation.id,
        level: 'subcounty',
      })
    } else if (currentLocation.type === 'parish' && currentLocation.parent) {
      // Add district
      if (currentLocation.parent.parent) {
        breadcrumbs.push({
          label: currentLocation.parent.parent.name,
          id: currentLocation.parent.parent.id,
          level: 'district',
        })
      }
      // Add subcounty
      breadcrumbs.push({
        label: currentLocation.parent.name,
        id: currentLocation.parent.id,
        level: 'subcounty',
      })
      // Add parish
      breadcrumbs.push({
        label: currentLocation.name,
        id: currentLocation.id,
        level: 'parish',
      })
    }

    return breadcrumbs
  }

  // Get list of locations at the same level for switching
  // Uses the locationsData passed in (which may be filtered)
  const getPeerLocations = () => {
    if (!currentLocation || !locationsData) return []

    if (currentLocation.type === 'district') {
      return locationsData.districts.map(d => ({
        id: d.id,
        name: d.name,
        type: 'district',
      }))
    } else if (currentLocation.type === 'subcounty' && currentLocation.parent) {
      // Find district in the filtered locationsData (not from parent object)
      const district = locationsData.districts.find(
        d => d.id === currentLocation.parent.id
      )
      return (
        district?.subcounties?.map(s => ({
          id: s.id,
          name: s.name,
          type: 'subcounty',
        })) || []
      )
    }

    return []
  }

  // Get child locations for drilling down
  // Uses the locationsData passed in (which may be filtered) instead of currentLocation.children
  const getChildLocations = () => {
    if (!currentLocation || !locationsData) return []

    if (currentLocation.type === 'district') {
      // Find district in filtered locationsData to get filtered subcounties
      const district = locationsData.districts.find(
        d => d.id === currentLocation.id
      )
      return (
        district?.subcounties?.map(c => ({
          id: c.id,
          name: c.name,
          type: 'subcounty',
        })) || []
      )
    } else if (currentLocation.type === 'subcounty') {
      // Find subcounty in filtered locationsData to get filtered parishes
      const district = locationsData.districts.find(d =>
        d.subcounties?.some(s => s.id === currentLocation.id)
      )
      const subcounty = district?.subcounties?.find(
        s => s.id === currentLocation.id
      )
      return (
        subcounty?.parishes?.map(c => ({
          id: c.id,
          name: c.name,
          type: 'parish',
        })) || []
      )
    }

    return []
  }

  const breadcrumbs = getBreadcrumbs()
  const peerLocations = getPeerLocations()
  const childLocations = getChildLocations()

  const handleLocationClick = locationId => {
    if (onLocationChange) {
      onLocationChange(locationId)
    }
    navigate(`/narrative/${locationId}`)
    setIsDropdownOpen(false)
  }

  const handleGoUp = () => {
    const parentBreadcrumb = breadcrumbs[breadcrumbs.length - 2]
    if (parentBreadcrumb && parentBreadcrumb.id !== 'national') {
      handleLocationClick(parentBreadcrumb.id)
    }
  }

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-4">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center justify-between mb-3">
            <nav className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <div key={crumb.id} className="flex items-center">
                  {index > 0 && (
                    <svg
                      className="w-4 h-4 text-gray-400 mx-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                  {crumb.id === 'national' ? (
                    <span className="text-gray-500">{crumb.label}</span>
                  ) : index === breadcrumbs.length - 1 ? (
                    <span className="font-semibold text-gray-900">
                      {crumb.label}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleLocationClick(crumb.id)}
                      className="text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      {crumb.label}
                    </button>
                  )}
                </div>
              ))}
            </nav>

            {/* Controls */}
            <div className="flex items-center gap-2">
              {/* Go Up Button */}
              {breadcrumbs.length > 2 && (
                <button
                  onClick={handleGoUp}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                  Go Up
                </button>
              )}

              {/* Location Switcher Dropdown */}
              {peerLocations.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                  >
                    Switch {currentLocation.type}
                    <svg
                      className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsDropdownOpen(false)}
                      />

                      {/* Dropdown Menu */}
                      <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                        {peerLocations.map(loc => (
                          <button
                            key={loc.id}
                            onClick={() => handleLocationClick(loc.id)}
                            className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                              loc.id === currentLocation.id
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-700'
                            }`}
                          >
                            {loc.name}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Drill Down to Child Locations */}
          {childLocations.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600">
                View{' '}
                {currentLocation.type === 'district' ? 'Subcounty' : 'Parish'}:
              </span>
              <div className="flex flex-wrap gap-2">
                {childLocations.slice(0, 8).map(child => (
                  <button
                    key={child.id}
                    onClick={() => handleLocationClick(child.id)}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
                  >
                    {child.name}
                  </button>
                ))}
                {childLocations.length > 8 && (
                  <span className="text-xs text-gray-500 py-1">
                    +{childLocations.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

LocationContextSelector.propTypes = {
  currentLocation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    parent: PropTypes.object,
    children: PropTypes.array,
  }),
  locationsData: PropTypes.object.isRequired,
  onLocationChange: PropTypes.func,
}

export default LocationContextSelector
